// ============================================================================
// Account Management — RAG card ordering (single source of truth).
//
// THE BUG THIS FIXES. My Accounts asked PostgREST for
// `.order('account_status_rag', { ascending: true })`. `account_status_rag` is
// TEXT, so "ascending" is ALPHABETICAL: Amber, Green, Red. The screen an AM
// opens to find trouble put its 12 Red buildings dead last, behind 172 Green
// ones. The severity order a person reads — Red, Amber, Green — is not a
// property of the string, so it has to be written down somewhere.
//
// WHY THIS IS A FILE AND NOT TWO LOCAL SORTS. Both call sites live in
// my-accounts.tsx (the fast card-view path and the "Show closed" base-table
// path) and they must agree: an AM who flips "Show closed" must not get a
// different ordering. That is the same reason lib/advance-gate.ts exists in
// Project Work — a rule that two places apply is a rule that lives in one
// place, or it drifts.
//
// WHY THE SORT IS CLIENT-SIDE. PostgREST's `.order()` takes a COLUMN, not an
// expression, so a `CASE` rank cannot be sent over the wire. The alternative
// would be a `rag_rank` column (or expression) on `v_am_account_cards` — a
// schema change to fix a display defect, and a second place the order could
// drift from this one. Neither view exposes a rank today; this is a display
// fix with no schema impact.
//
// UNSET RAG. `account_status_rag` is nullable. PostgREST's ascending order put
// NULLs last, so unset buildings sorted after Green. That placement is
// PRESERVED here deliberately — an unset RAG is not a severity, and moving it
// would be inventing a convention nobody asked for.
// ============================================================================

import type { RagStatus } from '../../../shared/types';

/**
 * Severity order, worst first. This is the whole convention — the ranks below
 * are derived from this array, so adding a status here is the only edit needed.
 */
export const RAG_ORDER: readonly RagStatus[] = ['Red', 'Amber', 'Green'];

/**
 * Sort rank for a RAG value. Lower sorts first. Anything not in RAG_ORDER —
 * NULL, an empty string, a value a future migration adds — ranks after every
 * known status, which is where PostgREST's NULLS LAST already put it.
 */
export function ragRank(rag: string | null | undefined): number {
  const i = RAG_ORDER.indexOf(rag as RagStatus);
  return i === -1 ? RAG_ORDER.length : i;
}

/** The minimum shape this comparator needs. Both call-site row types satisfy it. */
export interface RagOrderFields {
  account_status_rag: string | null;
  facility_name: string | null;
}

/**
 * Compare two facility rows: severity first (Red → Amber → Green → unset),
 * then alphabetically by facility name inside each block.
 *
 * The name tie-break is done HERE rather than left to the query's
 * `.order('facility_name')` so the helper is complete on its own and a caller
 * cannot half-apply it.
 */
export function compareByRagThenName(a: RagOrderFields, b: RagOrderFields): number {
  const d = ragRank(a.account_status_rag) - ragRank(b.account_status_rag);
  if (d !== 0) return d;
  return (a.facility_name ?? '').localeCompare(b.facility_name ?? '');
}

/**
 * Red block, then Amber, then Green, then unset — each block alphabetical by
 * facility name. Returns a new array; the input is not mutated.
 */
export function sortByRagThenName<T extends RagOrderFields>(rows: readonly T[]): T[] {
  return [...rows].sort(compareByRagThenName);
}
